import { fetchLiveCatalogStatus } from '@/redux/api/liveCatalogStatus';
import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import {
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
    LoadLiveCatalogRegistrationSuccessAction,
} from '@/redux/modules/actions/registerForCatalog';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.api';
import { getDeployment } from '@/redux/modules/config';
import { ApprovalStatus } from '@/types/Approval';
import {
    trackAuctionRegistrationSubmittedAnalytics,
    TrackAuctionRegistrationSubmittedType,
} from '@/utils/amplitude/analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

import { getLiveCatalogApproved } from '../../console';
import {
    getBidderCatalogApprovals,
    getCatalogRegistrationsForUserByIds,
    postAutoApproval,
    postSetCatalogNotify,
    postWelcomeModalAcknowledged,
} from './catalogRegistration.api';
import { getCatalogRegistration } from './catalogRegistration.selectors';
import {
    GetBidderCatalogApprovalsResponse,
    GetCatalogRegistrationsForUserByIdsResponse,
    PostAutoApprovalResponse,
    PostSetCatalogNotifyResponse,
    PostWelcomeModalAcknowledgedResponse,
} from './catalogRegistration.types';

/**
 * Submit user info for automatic catalog registration approval
 */
export const submitAutoApproval = createAsyncThunk<
    PostAutoApprovalResponse['payload'],
    { catalogId: number; itemId: number },
    AsyncThunkConfigWithRejectValue
>('la/ui/catalogRegistration/submitCatalogRegistrationAutoApproval', async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);
    const deployment = getDeployment(state);

    try {
        const { payload } = await postAutoApproval({
            authToken,
            bidderId,
            catalogId: params.catalogId,
            deployment,
            itemId: params.itemId,
        });

        if (payload.approvalStatus === ApprovalStatus.Approved || payload.approvalStatus === ApprovalStatus.Limited) {
            trackAuctionRegistrationSubmittedAnalytics({
                pageName: getAmplitudePageNameFromPath(window.location.pathname),
                registrationType: TrackAuctionRegistrationSubmittedType.Auto,
                url: getFullUrl(deployment, window.location.pathname),
            });
        }

        return payload;
    } catch {
        return rejectWithValue(new Error('Error in submitting catalog registration auto approval'));
    }
});

/**
 * Get the logged-in user's registrations for a given list of catalogIds
 */
export const fetchCatalogRegistrationsForUserByIdIfNeeded = createAsyncThunk<
    void,
    { catalogIds: number[]; force?: boolean }
>(
    'la/ui/catalogRegistration/fetchCatalogRegistrationsForUserByIdIfNeeded',
    async ({ catalogIds, force = false }, { dispatch, getState }) => {
        const shouldFetchRegistrations = Boolean(catalogIds.length) && isUserLoggedIn(getState());

        if (force || shouldFetchRegistrations) {
            await dispatch(loadCatalogRegistrationsForUserById({ catalogIds }));
            await dispatch(loadBidderCatalogApprovals());
        }

        return Promise.resolve();
    }
);

/**
 * fetches the live auction state for catalog, the user's bid limit for the current live item and the user's catalog registration
 * primarily used in live bidding
 */
export const loadLiveCatalogRegistrationById = createAsyncThunk<
    {
        approvalStatus: ApprovalStatus;
        approved: boolean;
        bidLimit: number;
        catalogId: number;
        created: number;
        wasApproved: boolean;
    },
    { catalogId: number; sellerId: number },
    AsyncThunkConfigWithRejectValue
>(
    'la/ui/catalogRegistration/loadLiveCatalogRegistrationById',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const wasApproved = getLiveCatalogApproved(state, params.catalogId);
        const bidderId = getBidderId(state);

        try {
            const liveAuctionState = await fetchLiveCatalogStatus({
                authToken,
                catalogId: params.catalogId,
                deployment,
                sellerId: params.sellerId,
            });

            const bidderApproval = await getBidLimitBalance({
                authToken,
                bidderId,
                catalogId: params.catalogId,
                deployment,
                itemId: liveAuctionState.data?.currentItem.itemId,
            });

            // console and consoleMessages rely on this action for the time being
            const action: LoadLiveCatalogRegistrationSuccessAction = {
                error: false,
                meta: {
                    actionTime: Date.now(),
                    catalogId: params.catalogId,
                },
                payload: {
                    approvalStatus: bidderApproval.payload.approvalStatus,
                    approved:
                        bidderApproval.payload.approvalStatus === ApprovalStatus.Approved ||
                        bidderApproval.payload.approvalStatus === ApprovalStatus.Limited,
                    bidLimit: bidderApproval.payload.bidLimit,
                    catalogId: params.catalogId,
                    created: Date.now(),
                    wasApproved,
                },
                type: LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
            };

            dispatch(action);

            return action.payload;
        } catch {
            return rejectWithValue(new Error('Error in fetching user catalog registrations'));
        }
    }
);

/**
 * fetches the live auction state for catalog, the user's bid limit for the current live item and the user's catalog registration
 * primarily used in live bidding
 */
export const fetchLiveCatalogRegistrationByIdIfNeeded = createAsyncThunk<
    void,
    { catalogId: number; force?: boolean; sellerId: number }
>(
    'la/ui/catalogRegistration/fetchLiveCatalogRegistrationByIdIfNeeded',
    async ({ catalogId, force = false, sellerId }, { dispatch, getState }) => {
        const shouldFetchRegistrations = Boolean(catalogId) && Boolean(sellerId) && isUserLoggedIn(getState());

        if (force || shouldFetchRegistrations) {
            await dispatch(loadLiveCatalogRegistrationById({ catalogId, sellerId }));
        }

        return Promise.resolve();
    }
);

/**
 * Turns on the user's catalog notifications for a given catalogId
 */
export const toggleCatalogNotifications = createAsyncThunk<
    PostSetCatalogNotifyResponse['data'],
    { catalogId: number; notify: boolean },
    AsyncThunkConfigWithRejectValue
>('la/ui/catalogRegistration/toggleCatalogNotifications', async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);
    const deployment = getDeployment(state);

    const catalogRegistration = getCatalogRegistration(state, params.catalogId);

    try {
        await postSetCatalogNotify({
            authToken,
            bidderId,
            catalogId: params.catalogId,
            deployment,
            notify: params.notify,
        });

        return {
            catalogId: params.catalogId,
            catalogRegistration: {
                ...catalogRegistration,
                notify: params.notify,
            },
        };
    } catch {
        return rejectWithValue(new Error('Error in setting catalog notifications for user'));
    }
});

/**
 * Records Welcome to the Auction acknowledgement for a catalog. The reducer sets the flag on
 * pending because statuses is read from a replica and an immediate refetch can still be false.
 */
export const acknowledgeWelcomeModal = createAsyncThunk<
    PostWelcomeModalAcknowledgedResponse['payload'],
    { catalogId: number },
    AsyncThunkConfigWithRejectValue
>('la/ui/catalogRegistration/acknowledgeWelcomeModal', async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);
    const deployment = getDeployment(state);

    try {
        const { payload } = await postWelcomeModalAcknowledged({
            authToken,
            bidderId,
            catalogId: params.catalogId,
            deployment,
        });

        return payload;
    } catch {
        return rejectWithValue(new Error('Error acknowledging welcome modal'));
    }
});

/**
 * Get the logged-in user's registrations for a given list of catalogIds
 */
export const loadCatalogRegistrationsForUserById = createAsyncThunk<
    GetCatalogRegistrationsForUserByIdsResponse['payload'],
    { catalogIds: number[] },
    AsyncThunkConfigWithRejectValue
>('la/ui/catalogRegistration/loadCatalogRegistrationsForUserById', async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);
    const deployment = getDeployment(state);

    try {
        const { payload } = await getCatalogRegistrationsForUserByIds({
            authToken,
            bidderId,
            catalogIds: params.catalogIds,
            deployment,
        });

        return payload;
    } catch {
        return rejectWithValue(new Error('Error in fetching user catalog registrations'));
    }
});

export const loadBidderCatalogApprovals = createAsyncThunk<
    GetBidderCatalogApprovalsResponse['payload'],
    undefined,
    AsyncThunkConfigWithRejectValue
>('la/ui/catalogRegistration/loadBidderCatalogApprovals', async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);
    const deployment = getDeployment(state);

    try {
        const { payload } = await getBidderCatalogApprovals({
            authToken,
            bidderId,
            deployment,
        });

        return payload;
    } catch {
        return rejectWithValue(new Error('Error in fetching user catalog registrations'));
    }
});
